import React, { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { useNavigate } from 'react-router-dom';
import { User, Mail, Calendar, Shield, CreditCard, Heart, Home, ShoppingBag, MapPin } from 'lucide-react';
import Button from '../components/common/Button';

const ProfilePage: React.FC = () => {
  const { user, isAuthenticated, logout, refreshUser } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [counts, setCounts] = useState({ favorites: 0, listings: 0, purchasedContacts: 0 });

  useEffect(() => {
    if (user) {
      refreshUser();
    }
  }, []);

  useEffect(() => {
    if (user) {
      setCounts({
        favorites: user.favorites?.length || 0,
        listings: user.listings?.length || 0,
        purchasedContacts: user.purchasedContacts?.length || 0
      });
    }
  }, [user?.favorites?.length, user?.listings?.length, user?.purchasedContacts?.length]);

  useEffect(() => {
    if (!isAuthenticated || !user) {
      navigate('/login');
    }
  }, [isAuthenticated, user, navigate]);

  if (!isAuthenticated || !user) {
    return null;
  }

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  return (
    <div className="min-h-screen bg-neutral-50 py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-neutral-900 mb-2">
            {t('profile.title')}
          </h1>
          <p className="text-neutral-600">
            {t('profile.subtitle')}
          </p>
        </div>

        {/* Profile Card */}
        <div className="bg-white rounded-2xl shadow-soft border border-neutral-200 mb-8">
          <div className="p-8">
            <div className="flex items-center space-x-6 mb-6">
              {/* Avatar */}
              <div className="relative">
                {user.avatar ? (
                  <img
                    src={user.avatar}
                    alt={user.name}
                    className="w-24 h-24 rounded-2xl object-cover shadow-medium"
                  />
                ) : (
                  <div className="w-24 h-24 rounded-2xl bg-brand-dark flex items-center justify-center shadow-medium">
                    <span className="text-white font-bold text-2xl">
                      {user.name.charAt(0).toUpperCase()}
                    </span>
                  </div>
                )}
                {/* Provider badge */}
                <div className="absolute -bottom-2 -right-2">
                  <div className="bg-white rounded-full p-2 shadow-medium border border-neutral-200">
                    {user.provider === 'google' && (
                      <div className="w-6 h-6 bg-red-500 rounded-full flex items-center justify-center">
                        <span className="text-white text-xs font-bold">G</span>
                      </div>
                    )}
                    {user.provider === 'facebook' && (
                      <div className="w-6 h-6 bg-primary-500 rounded-full flex items-center justify-center">
                        <span className="text-white text-xs font-bold">F</span>
                      </div>
                    )}
                    {user.provider === 'email' && (
                      <Mail size={16} className="text-neutral-600" />
                    )}
                  </div>
                </div>
              </div>

              {/* Basic Info */}
              <div className="flex-1">
                <h2 className="text-2xl font-bold text-neutral-900 mb-2">
                  {user.name}
                </h2>
                <p className="text-neutral-600 mb-4">
                  {user.email}
                </p>
                <div className="flex items-center space-x-4 text-sm text-neutral-500">
                  <div className="flex items-center">
                    <Shield size={16} className="mr-1" />
                    <span>{t('profile.verifiedAccount')}</span>
                  </div>
                  <div className="flex items-center">
                    <Calendar size={16} className="mr-1" />
                    <span>{t('profile.activeUser')}</span>
                  </div>
                </div>
              </div>

              {/* Tokens */}
              <div className="text-center">
                <div className="bg-accent-50 rounded-2xl p-6 border border-accent-200">
                  <div className="flex items-center justify-center mb-2">
                    <CreditCard size={24} className="text-accent-600 mr-2" />
                    <span className="text-2xl font-bold text-accent-700">
                      {user.tokens}
                    </span>
                  </div>
                  <p className="text-sm text-accent-600 font-medium">
                    {t('common.tokens')}
                  </p>
                  <Button
                    size="sm"
                    variant="outline"
                    className="mt-3 border-accent-300 text-accent-700 hover:bg-accent-50"
                    onClick={() => navigate('/buy-tokens')}
                  >
                    {t('common.buyTokens')}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Statistics Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          {/* Favorites */}
          <div className="bg-white rounded-2xl shadow-soft border border-neutral-200 p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center">
                <Heart size={24} className="text-red-500 mr-3" />
                <h3 className="text-lg font-semibold text-neutral-900">
                  {t('profile.favorites')}
                </h3>
              </div>
              <span className="text-2xl font-bold text-neutral-700">
                {counts.favorites}
              </span>
            </div>
            <p className="text-sm text-neutral-600 mb-4">
              {t('profile.favoritesDescription')}
            </p>
            <Button
              variant="outline"
              size="sm"
              fullWidth
              onClick={() => navigate('/favorites')}
            >
              {t('profile.viewFavorites')}
            </Button>
          </div>

          {/* My Properties */}
          <div className="bg-white rounded-2xl shadow-soft border border-neutral-200 p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center">
                <Home size={24} className="text-primary-500 mr-3" />
                <h3 className="text-lg font-semibold text-neutral-900">
                  {t('profile.myListings')}
                </h3>
              </div>
              <span className="text-2xl font-bold text-neutral-700">
                {counts.listings}
              </span>
            </div>
            <p className="text-sm text-neutral-600 mb-4">
              {t('profile.myListingsDescription')}
            </p>
            <Button
              variant="outline"
              size="sm"
              fullWidth
              onClick={() => navigate('/my-properties')}
            >
              {t('profile.manageListings')}
            </Button>
          </div>

          {/* Purchased Contacts */}
          <div className="bg-white rounded-2xl shadow-soft border border-neutral-200 p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center">
                <ShoppingBag size={24} className="text-primary-500 mr-3" />
                <h3 className="text-lg font-semibold text-neutral-900">
                  {t('profile.purchasedContacts')}
                </h3>
              </div>
              <span className="text-2xl font-bold text-neutral-700">
                {counts.purchasedContacts}
              </span>
            </div>
            <p className="text-sm text-neutral-600 mb-4">
              {t('profile.purchasedContactsDescription')}
            </p>
            <Button
              variant="outline"
              size="sm"
              fullWidth
              onClick={() => navigate('/purchased-contacts')}
            >
              {t('profile.viewContacts')}
            </Button>
          </div>
        </div>

        {/* Account Information */}
        <div className="bg-white rounded-2xl shadow-soft border border-neutral-200 mb-8">
          <div className="p-6 border-b border-neutral-200">
            <h3 className="text-lg font-semibold text-neutral-900 mb-1">
              {t('profile.accountInfo')}
            </h3>
            <p className="text-sm text-neutral-600">
              {t('profile.accountInfoSubtitle')}
            </p>
          </div>
          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-2">
                  {t('profile.fullName')}
                </label>
                <div className="p-3 bg-neutral-50 rounded-lg border border-neutral-200">
                  <div className="flex items-center">
                    <User size={16} className="text-neutral-400 mr-2" />
                    <span className="text-neutral-900">{user.name}</span>
                  </div>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-2">
                  {t('profile.emailAddress')}
                </label>
                <div className="p-3 bg-neutral-50 rounded-lg border border-neutral-200">
                  <div className="flex items-center">
                    <Mail size={16} className="text-neutral-400 mr-2" />
                    <span className="text-neutral-900">{user.email}</span>
                  </div>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-2">
                  {t('profile.loginMethod')}
                </label>
                <div className="p-3 bg-neutral-50 rounded-lg border border-neutral-200">
                  <div className="flex items-center">
                    <Shield size={16} className="text-neutral-400 mr-2" />
                    <span className="text-neutral-900 capitalize">
                      {user.provider === 'email' ? t('profile.emailPassword') : 
                       user.provider === 'google' ? t('profile.googleAccount') :
                       user.provider === 'facebook' ? t('profile.facebookAccount') : 
                       user.provider}
                    </span>
                  </div>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-2">
                  {t('profile.userId')}
                </label>
                <div className="p-3 bg-neutral-50 rounded-lg border border-neutral-200">
                  <div className="flex items-center">
                    <MapPin size={16} className="text-neutral-400 mr-2" />
                    <span className="text-neutral-900 font-mono text-sm">
                      {user.id}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="bg-white rounded-2xl shadow-soft border border-neutral-200">
          <div className="p-6 border-b border-neutral-200">
            <h3 className="text-lg font-semibold text-neutral-900 mb-1">
              {t('profile.actions')}
            </h3>
            <p className="text-sm text-neutral-600">
              {t('profile.actionsSubtitle')}
            </p>
          </div>
          <div className="p-6">
            <div className="flex flex-col sm:flex-row gap-4">
              <Button
                variant="outline"
                onClick={() => navigate('/buy-tokens')}
                className="flex-1"
              >
                <CreditCard size={16} className="mr-2" />
                {t('common.buyTokens')}
              </Button>
              <Button
                variant="outline"
                onClick={() => navigate('/add-property')}
                className="flex-1"
              >
                <Home size={16} className="mr-2" />
                {t('common.addProperty')}
              </Button>
              <Button
                variant="outline"
                onClick={handleLogout}
                className="flex-1 text-red-600 border-red-200 hover:bg-red-50"
              >
                {t('common.logout')}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProfilePage; 